































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import SectionWrapper from '@/componentsV2/molecules/SectionWrapper/SectionWrapper.vue';
import PerformanceItem, {
  HighlightContent
} from '@/components/molecules/PerformanceItem/PerformanceItem.vue';
import CtaCard from '@/componentsV2/molecules/CtaCard/CtaCard.vue';
import ModalOptions from '@/components/Modal/ModalOptions';

import { ICardPerformance } from '@/models/performance';

import PerformanceOverviewService from '@/api/PerformanceOverview';
import BusinessService from '@/api/BusinessService';

import eventBus from '@/utils/eventBus';
import DateUtils from '@/utils/DateUtils';
import { goToElement } from '@/utils/goToElement';

import customerInactivity from '@/constants/customerInactivity';
import { ERouteDisclaimerMessage } from '@/router/validators/enums';
import { sellCoinsValidator } from '@/router/validators/navbar';
import { EBusinessReportType } from '@/models/business';

interface ITotalValue extends Partial<ICardPerformance> {
  counterFractionDigits?: number;
  highlighted?: boolean;
  highlightContent?: HighlightContent;
  showInfoIcon?: boolean;
  infoIconTooltip?: string;
  actionLabel?: string;
  reportType?: string[];
}

@Component({
  components: {
    SectionWrapper,
    PerformanceItem,
    CtaCard
  }
})
export default class PerformanceOverview extends Vue {
  @Prop({ default: true }) showCta?: boolean;

  loaded = false;
  performanceOverviewService = new PerformanceOverviewService();
  businessService = new BusinessService();
  totalValues: Record<string, ITotalValue> = {
    totalCustomers: {
      counter: 0,
      link: '/performance/customer-center',
      disabled: true,
      reportType: [
        EBusinessReportType.OFFER,
        EBusinessReportType.OFFER_COINS,
        EBusinessReportType.COIN
      ]
    },
    activeCustomers: {
      counter: 0,
      link: '/performance/customer-center?customerListFilter=active',
      disabled: true,
      reportType: [
        EBusinessReportType.OFFER,
        EBusinessReportType.OFFER_COINS,
        EBusinessReportType.COIN
      ]
    },
    totalNoMembersReached: {
      counter: 0,
      link: '/performance/activities',
      disabled: true,
      reportType: [EBusinessReportType.OFFER]
    },
    totalOfferRedemptions: {
      counter: 0,
      link: '/performance/activities',
      disabled: true,
      reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS]
    },
    redeemedCoins: {
      counter: 0,
      link: '/performance/activities',
      disabled: true,
      reportType: [EBusinessReportType.OFFER_COINS, EBusinessReportType.COIN]
    },
    paymentRelatedToRedemptions: {
      counter: 0,
      link: '/performance/activities',
      disabled: true,
      prefixCounter: '',
      counterFractionDigits: 2,
      reportType: [EBusinessReportType.OFFER_COINS, EBusinessReportType.COIN]
    },
    impressions: {
      counter: 0,
      link: '/performance/activities',
      disabled: true,
      showInfoIcon: true,
      infoIconTooltip:
        'Reporting since: Jun 28, 2024.<br/><a href="https://support.twism.com/hc/en-us/articles/29210657024525-What-are-Business-Impressions" target="__blank" style="font-weight: 600; text-decoration: underline">Click here to learn more.</a>',
      reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS]
    }
  };

  observer: IntersectionObserver | null = null;
  triggerIntersected = false;

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get tPrefix() {
    return 'performance.overview';
  }

  get tPrefixSpecialCoinTerm() {
    return `common.specialCoin`;
  }

  get programLaunched() {
    return DateUtils.formattedDate({
      date: this.businessInfo.createdAt,
      applyTimezone: true
    });
  }

  get configSection() {
    return {
      descriptionMaxWidth: '480px',
      padding: '0px',
      background: 'transparent',
      titleFontSize: '24px'
    };
  }

  get customerInactivityDaysConfig() {
    return this.business.inactiveCustomerDays;
  }

  get countryData() {
    return this.$store.getters['internationalization/getCountrySettings'];
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'];
  }

  get reportType() {
    return this.businessInfo.reportType;
  }

  get transformedTotalValues() {
    const result = Object.entries(this.totalValues)
      .map(([key, value]) => ({
        ...value,
        ...(value.hasOwnProperty('prefixCounter') && {
          prefixCounter: this.currencySymbol
        }),
        label: String(this.$t(`${this.tPrefix}.overview.cards.${key}.label`)),
        description: String(
          this.$t(`${this.tPrefix}.overview.cards.${key}.description`, {
            numberOfDays:
              this.customerInactivityDaysConfig ||
              customerInactivity.DEFAULT_DAYS,
            pluralDays: this.$tc(
              'common.pluralize.day',
              this.customerInactivityDaysConfig ||
                customerInactivity.DEFAULT_DAYS
            )
          })
        ),
        linkLabel: 'See report',
        slug: key
      }))
      .filter(item =>
        item.reportType ? item.reportType.includes(this.reportType) : true
      );

    return result;
  }

  get currencySymbol() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  get business() {
    const { business = null } = this.$store.getters['business/getBusinessInfo'];

    return business;
  }

  get startMovingList() {
    return [
      {
        link: '/marketing-center/offer-free-coins',
        label: 'Welcome Coins',
        icon: 'WelcomeCoinsIcon'
      },
      {
        link: '/special-coins-offers',
        label: `${this.$t(`${this.tPrefixSpecialCoinTerm}.plural`)}`,
        icon: 'SpecialCoinIcon',
        disabled: !sellCoinsValidator().isValid,
        disclaimer:
          !sellCoinsValidator().isValid &&
          String(
            this.$t(
              `common.routeDisclaimers.${
                ERouteDisclaimerMessage.SELL_COINS_RESTRICTION
              }`,
              {
                country: this.business.country
              }
            )
          )
      },
      {
        link: '/marketing-center/social-media-templates',
        label: 'Create Social Media Assets',
        icon: 'SocialMediaIcon'
      }
    ];
  }

  get isEmptyData() {
    return Object.values(this.totalValues).every(
      ({ counter }) => Number(counter) === 0
    );
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get isImpersonated() {
    return this.$store.getters['manager/isImpersonated'];
  }

  created() {
    this.getOverview();
  }

  mounted() {
    this.handleTriggerObserver();
  }

  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  }

  handleTriggerObserver() {
    if (!this.isMobileDevice) return;

    const target = document.querySelector(
      '.performance-overview__grid-trigger'
    );

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.triggerIntersected = entry.isIntersecting;
      },
      {
        root: document.querySelector('.performance-overview__grid'),
        threshold: 0
      }
    );

    this.observer.observe(target as HTMLElement);
  }

  handleGoTo(route: string) {
    if (!route.includes('#')) return;

    const anchorHash = route.substring(route.indexOf('#'));

    goToElement(anchorHash);
  }

  async getOverview() {
    this.$showLoaderWithLocker();

    const response = await this.performanceOverviewService.getNewOverview();

    this.$hideLoader();

    if (response.status !== 200) return;

    const dataToUpdate = Object.keys(this.totalValues);

    const entries = Object.entries(response.data);

    let newTotalValues = this.totalValues;

    for (const entry of entries) {
      const [key, value] = entry;

      if (!dataToUpdate.includes(key)) continue;

      const totalValue = newTotalValues[key];

      Object.assign(totalValue, {
        counter: value,
        disabled: Number(value) === 0
      });
    }

    this.totalValues = newTotalValues;
    this.loaded = true;
  }

  handleStartMoving() {
    eventBus.$emit(
      'openSubModal',
      new ModalOptions(
        '',
        '',
        'none',
        true,
        '',
        '',
        '',
        false,
        'startMovingModal',
        false,
        true,
        true,
        true,
        true
      ),
      {
        title: 'Attract and activate your customers',
        list: this.startMovingList
      }
    );

    this.$analytics({
      eventCategory: 'Perfomance Overview',
      eventLabel: 'Start creating data!',
      eventAction: 'click'
    });
  }
}
